import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import { IProxyEvent } from "src/store/proxyEvent/types";
import { PROXY_EVENT_LABEL } from "src/constants/labels/proxyEventLabel";
import { VotingProxyEventsView } from ".";
import { VOTING_PROXY_EVENTS_VIEW } from ".";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const UPCOMING_COLUMNS = [
  "type",
  "issuer",
  "recordDate",
  "solicitor",
  "tabulator",
];

export const ACTIVE_COLUMNS = [
  "type",
  "issuer",
  "recordDate",
  "solicitor",
  "tabulator",
  "voteCutoff",
  "meetingDate",
];

export const RECENTLY_COMPLETED_COLUMNS = [
  "type",
  "issuer",
  "recordDate",
  "solicitor",
  "tabulator",
  "voteCutoff",
  "meetingDate",
];

export const HISTORICAL_COLUMNS = [
  "type",
  "issuer",
  "recordDate",
  "solicitor",
  "tabulator",
  "voteCutoff",
  "meetingDate",
];

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type UpcomingColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  tabulator: ColumnType;
};

export type ActiveColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  tabulator: ColumnType;
  voteCutoff: ColumnType;
  meetingDate: ColumnType;
};

export type RecentlyCompletedColumnData = {
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  tabulator: ColumnType;
  voteCutoff: ColumnType;
  meetingDate: ColumnType;
};

export type HistoricalColumnData = {
  issuer: ColumnType;
  type: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  tabulator: ColumnType;
  voteCutoff: ColumnType;
  meetingDate: ColumnType;
};

export function getColumns(view: VotingProxyEventsView) {
  if (view === VOTING_PROXY_EVENTS_VIEW.UPCOMING) {
    return UPCOMING_COLUMNS;
  } else if (view === VOTING_PROXY_EVENTS_VIEW.ACTIVE) {
    return ACTIVE_COLUMNS;
  } else if (view === VOTING_PROXY_EVENTS_VIEW.RECENTLY_COMPLETED) {
    return RECENTLY_COMPLETED_COLUMNS;
  } else {
    return HISTORICAL_COLUMNS;
  }
}

export function getColumnData({
  getSort,
  onOrderingChange,
}: ColumnDataHelpers):
  | UpcomingColumnData
  | ActiveColumnData
  | RecentlyCompletedColumnData
  | HistoricalColumnData {
  return {
    type: {
      sort: getSort("type"),
      sortFn: (data: unknown) => onOrderingChange(data, "type"),
      style: "text",
      title: "Type",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    issuer: {
      sort: getSort("issuer__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
      style: "text",
      title: "Issuer",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    recordDate: {
      sort: getSort("recordDate"),
      sortFn: (data: unknown) => onOrderingChange(data, "recordDate"),
      style: "text",
      title: "Record Date",
    },
    solicitor: {
      sort: getSort("solicitor__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "solicitor__name"),
      style: "text",
      title: "Solicitor",
    },
    tabulator: {
      sort: getSort("tabulator__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "tabulator__name"),
      style: "text",
      title: "Tabulator",
    },
    voteCutoff: {
      sort: getSort("voteCutoff"),
      sortFn: (data: unknown) => onOrderingChange(data, "voteCutoff"),
      style: "text",
      title: "Vote Cutoff",
    },
    meetingDate: {
      sort: getSort("meetingDate"),
      sortFn: (data: unknown) => onOrderingChange(data, "meetingDate"),
      style: "text",
      title: "Meeting Date",
    },
  };
}

export function getRowData(
  events: IProxyEvent[],
  onRowClick: (event: IProxyEvent) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { event } = data as {
      event: IProxyEvent;
      idx: string;
    };
    onRowClick(event);
  };

  return events.map((event, idx) => {
    const {
      id,
      issuer,
      type,
      recordDate,
      solicitor,
      tabulator,
      voteCutoff,
      meetingDate,
    } = event;

    return {
      id,
      data: { event, idx },
      rowData: {
        issuer: {
          content: issuer.name,
          clickHandler: handleRowClick,
        },
        type: {
          content: PROXY_EVENT_LABEL[type],
          clickHandler: handleRowClick,
        },
        recordDate: {
          content: dayjs(recordDate).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        solicitor: {
          content: solicitor.name,
          clickHandler: handleRowClick,
        },
        tabulator: {
          content: tabulator ? tabulator.name : "-",
          clickHandler: handleRowClick,
        },
        voteCutoff: {
          content: dayjs(voteCutoff).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        meetingDate: {
          content: dayjs(meetingDate).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
      },
    };
  });
}
